<script setup lang="ts">
import { Components } from '@/api/generated/api';
import CustomButton from '@/core/elements/CustomButton.vue';
import TextInput from '@/core/elements/TextInput.vue';
import BillingCheckoutIllustrationPanel from './BillingCheckoutIllustrationPanel.vue';
import { computed, reactive, watch } from 'vue';
import { object, string } from 'zod';
import { BILLING_INVOICE_ADDRESS_LIMITS } from 'common/constants/billing.constants';
import {
  getBillingTaxIdExample,
  isBillingTaxIdFormatValid,
  normalizeBillingTaxId,
} from 'common/utils/billing-tax-id.utils';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  address?: Components.Schemas.BillingInvoiceAddressDto | null;
  draftAddress?: Components.Schemas.UpsertTenantInvoiceAddressDto | null;
  disabled?: boolean;
  title?: string;
  submitLabel?: string;
}>();

const emit = defineEmits<{
  back: [];
  continue: [value: Components.Schemas.UpsertTenantInvoiceAddressDto];
  'draft-change': [value: Components.Schemas.UpsertTenantInvoiceAddressDto];
}>();

type InvoiceAddressForm = {
  name: string;
  line1: string;
  city: string;
  postalCode: string;
  vatId: string;
  notes: string;
};

const form = reactive<InvoiceAddressForm>({
  name: '',
  line1: '',
  city: '',
  postalCode: '',
  vatId: '',
  notes: '',
});

const touched = reactive<Record<keyof InvoiceAddressForm, boolean>>({
  name: false,
  line1: false,
  city: false,
  postalCode: false,
  vatId: false,
  notes: false,
});

const requiredFields: Array<keyof InvoiceAddressForm> = ['name', 'line1', 'city', 'postalCode'];

const countryIsoCode = computed<string | null>(() => props.address?.countryIsoCode ?? null);
const vatIdExample = computed<string>(() => getBillingTaxIdExample(countryIsoCode.value));

const invoiceAddressSchema = computed(() =>
  object({
    name: string().trim().min(1).max(BILLING_INVOICE_ADDRESS_LIMITS.name),
    line1: string().trim().min(1).max(BILLING_INVOICE_ADDRESS_LIMITS.line1),
    city: string().trim().min(1).max(BILLING_INVOICE_ADDRESS_LIMITS.city),
    postalCode: string().trim().min(1).max(BILLING_INVOICE_ADDRESS_LIMITS.postalCode),
    vatId: string()
      .trim()
      .max(BILLING_INVOICE_ADDRESS_LIMITS.vatId)
      .transform(normalizeBillingTaxId)
      .refine((value) => isBillingTaxIdFormatValid(value, countryIsoCode.value), {
        message: t('billing.invoicePayment.invalidVatId', { example: vatIdExample.value }),
      }),
    notes: string().trim().max(BILLING_INVOICE_ADDRESS_LIMITS.notes),
  }),
);

const validationResult = computed(() =>
  invoiceAddressSchema.value.safeParse({
    name: form.name,
    line1: form.line1,
    city: form.city,
    postalCode: form.postalCode,
    vatId: form.vatId,
    notes: form.notes,
  }),
);

const fieldErrorMessages = computed<Partial<Record<keyof InvoiceAddressForm, string>>>(() => {
  if (validationResult.value.success) return {};

  return validationResult.value.error.issues.reduce<Partial<Record<keyof InvoiceAddressForm, string>>>((acc, issue) => {
    const field = issue.path[0];
    if (typeof field === 'string' && field in form) {
      acc[field as keyof InvoiceAddressForm] = issue.message;
    }
    return acc;
  }, {});
});

const canSubmit = computed<boolean>(() => !props.disabled && validationResult.value.success);
const title = computed<string>(() => props.title ?? t('billing.invoicePayment.addressTitle'));
const submitLabel = computed<string>(() => props.submitLabel ?? t('billing.invoicePayment.createInvoice'));
const vatIdErrorMessage = computed<string | undefined>(() => {
  if (!form.vatId.trim()) return undefined;
  return fieldErrorMessages.value.vatId;
});

const hasError = (field: keyof InvoiceAddressForm): boolean => {
  return touched[field] && !!fieldErrorMessages.value[field];
};

const markTouched = (field: keyof InvoiceAddressForm): void => {
  touched[field] = true;
};

const onSubmit = (): void => {
  for (const field of requiredFields) touched[field] = true;
  if (!validationResult.value.success) return;
  const parsedAddress = validationResult.value.data;

  emit('continue', {
    name: parsedAddress.name,
    line1: parsedAddress.line1,
    city: parsedAddress.city,
    postalCode: parsedAddress.postalCode,
    vatId: parsedAddress.vatId || null,
    notes: parsedAddress.notes || null,
  });
};

const emitDraftChange = (): void => {
  emit('draft-change', {
    name: form.name,
    line1: form.line1,
    city: form.city,
    postalCode: form.postalCode,
    vatId: form.vatId,
    notes: form.notes,
  });
};

watch(
  () => [props.address, props.draftAddress] as const,
  ([address, draftAddress]) => {
    const source = draftAddress ?? address;
    form.name = source?.name ?? '';
    form.line1 = source?.line1 ?? '';
    form.city = source?.city ?? '';
    form.postalCode = source?.postalCode ?? '';
    form.vatId = source?.vatId ?? '';
    form.notes = source?.notes ?? '';
  },
  { immediate: true },
);

watch(form, emitDraftChange, { deep: true });
</script>

<template>
  <form class="flex h-full min-h-0 overflow-hidden bg-surface-primary max-md:flex-col" @submit.prevent="onSubmit">
    <BillingCheckoutIllustrationPanel />

    <div class="flex h-full min-w-0 flex-1 flex-col bg-surface-primary px-xxl py-xxl max-md:p-lg max-md:pt-xxl">
      <div class="min-h-0 flex-1 overflow-y-auto">
        <div class="flex h-full min-h-[420px] flex-col gap-xl max-md:min-h-0 max-md:gap-lg">
          <h1 class="text-title text-primary max-md:text-heading">
            {{ title }}
          </h1>

          <div class="flex flex-1 flex-col gap-md max-md:gap-sm">
            <TextInput
              v-model="form.name"
              :label="$t('billing.invoicePayment.fields.name')"
              :has-errors="hasError('name')"
              :maxlength="BILLING_INVOICE_ADDRESS_LIMITS.name"
              :disabled="disabled"
              @commit="markTouched('name')"
            />
            <TextInput
              v-model="form.line1"
              :label="$t('billing.invoicePayment.fields.line1')"
              :has-errors="hasError('line1')"
              :maxlength="BILLING_INVOICE_ADDRESS_LIMITS.line1"
              :disabled="disabled"
              @commit="markTouched('line1')"
            />
            <div class="grid grid-cols-2 gap-md max-md:grid-cols-1">
              <TextInput
                v-model="form.city"
                :label="$t('billing.invoicePayment.fields.city')"
                :has-errors="hasError('city')"
                :maxlength="BILLING_INVOICE_ADDRESS_LIMITS.city"
                :disabled="disabled"
                @commit="markTouched('city')"
              />
              <TextInput
                v-model="form.postalCode"
                :label="$t('billing.invoicePayment.fields.postalCode')"
                :has-errors="hasError('postalCode')"
                :maxlength="BILLING_INVOICE_ADDRESS_LIMITS.postalCode"
                :disabled="disabled"
                @commit="markTouched('postalCode')"
              />
            </div>
            <TextInput
              v-model="form.vatId"
              :label="$t('billing.invoicePayment.fields.vatId')"
              :has-errors="!!vatIdErrorMessage"
              :maxlength="BILLING_INVOICE_ADDRESS_LIMITS.vatId"
              :disabled="disabled"
              @commit="markTouched('vatId')"
            />
            <p v-if="vatIdErrorMessage" class="-mt-sm text-right text-footer text-error">
              {{ vatIdErrorMessage }}
            </p>
            <textarea
              v-model="form.notes"
              class="input-text min-h-[104px] resize-none overflow-y-auto whitespace-pre-wrap break-words px-md py-md [overflow-wrap:anywhere] max-md:min-h-[76px]"
              :maxlength="BILLING_INVOICE_ADDRESS_LIMITS.notes"
              :disabled="disabled"
              :aria-label="$t('billing.invoicePayment.fields.notes')"
              :placeholder="$t('billing.invoicePayment.fields.notes')"
            ></textarea>
          </div>
        </div>
      </div>

      <div class="flex shrink-0 items-center justify-end gap-md pt-md max-md:pb-mobile-safe-bottom-lg">
        <CustomButton type="button" theme="secondary" :disabled="disabled" @click="emit('back')">
          {{ $t('general.back') }}
        </CustomButton>
        <CustomButton class="w-min justify-center whitespace-nowrap" :disabled="!canSubmit" type="submit">
          {{ submitLabel }}
        </CustomButton>
      </div>
    </div>
  </form>
</template>
